import { get, isEmpty, isNil } from 'lodash'
import * as Types from '../types'
import filterCoursesByQuestionCategory from '../../utils/local/filter-courses-by-question-category'
import hasZeroMinutesInCourses from '../../utils/local/has-zero-minutes-in-courses'
import hasZeroStudentsEnrolled from '../../utils/local/has-zero-students-enrolled'
import isMissingInstructionalDepth from '../../utils/local/is-missing-instructional-depth'
import isMissingMinutesInCourses from '../../utils/local/is-missing-minutes-in-courses'
import isMissingNumberOfStudentsEnrolled from '../../utils/local/is-missing-number-of-students-enrolled'

const {
  INSTRUCTIONAL_DEPTH_COURSE_FORM,
  WEEKLY_MINUTES_COURSE_FORM,
  ARTS_INSTRUCTOR_FORM,
  SINGLE_STANDARD_FORM,
  MULTIPLE_STANDARDS_FORM,
  COURSE_SCHEDULE_FORM,
  FUNDING_FORM,
  CUSTOM_QUESTION_FORM,
  SPACE_TYPES_FORM,
  ARTS_EDUCATION_RESOURCES_FORM,
  INSTRUCTIONAL_TIME_MATRIX_FORM,
} = Types.QUESTION_DISPLAY_COMPONENTS

const K_THROUGH_EIGHT_CATEGORIES = [
  Types.ELEMENTARY_SCHOOL_TYPE,
  Types.MIDDLE_SCHOOL_TYPE,
]

function hasCompleteHighSchoolCourses(school) {
  const courses = filterCoursesByQuestionCategory(
    get(school, 'courses') || [],
    Types.HIGH_SCHOOL_TYPE
  )

  if (isEmpty(courses)) return false

  return !(
    isMissingInstructionalDepth(courses) ||
    isMissingNumberOfStudentsEnrolled(courses) ||
    hasZeroStudentsEnrolled(courses)
  )
}

function hasCompleteKThroughEightCourses(school) {
  const courses = filterCoursesByQuestionCategory(
    get(school, 'courses') || [],
    K_THROUGH_EIGHT_CATEGORIES
  )

  if (isEmpty(courses)) return false

  return !(
    isMissingMinutesInCourses(courses) ||
    isMissingNumberOfStudentsEnrolled(courses) ||
    hasZeroMinutesInCourses(courses) ||
    hasZeroStudentsEnrolled(courses)
  )
}

function hasCompleteInstructors(school) {
  const instructors = get(school, 'instructors') || []

  if (isEmpty(instructors)) return false

  return instructors.every((instructor) => {
    if (!instructor.schedule) return false
    if (instructor.primaryFocus) return true

    const focuses = instructor.focuses
    if (!Array.isArray(focuses)) return false

    return focuses.some(
      (focus) =>
        focus && focus.primary && (focus.focusableId || focus.focusable_id)
    )
  })
}

function answeredDisciplineIds(allocations) {
  return new Set(
    (allocations || [])
      .map((allocation) => get(allocation, 'secondaryResourceable.id'))
      .filter((id) => !isNil(id))
  )
}

/**
 * Every discipline needs both a frequency and a class duration. Schools that
 * don't teach a discipline answer "None" rather than leaving the row blank.
 * When the discipline list hasn't loaded we can't check coverage, so we fall
 * back to requiring at least one complete row rather than blocking outright.
 */
function hasCompleteInstructionalTime(school, disciplines) {
  const frequencies = answeredDisciplineIds(
    get(school, 'instructionalFrequencies')
  )
  const durations = answeredDisciplineIds(get(school, 'classDurations'))

  if (isEmpty(disciplines))
    return [...frequencies].some((id) => durations.has(id))

  return disciplines.every(({ id }) => frequencies.has(id) && durations.has(id))
}

function hasCustomAnswer(school, surveyQuestionId) {
  return (get(school, 'surveyAnswers') || []).some(
    (answer) => answer.surveyQuestionId === surveyQuestionId
  )
}

/**
 * Whether a survey question is shown to the given school. Course questions are
 * split by grade level, so a question can be attached to a survey without ever
 * being presented to every school taking it.
 */
export function isQuestionApplicable(question, school) {
  const category = get(school, 'category')

  switch (question.displayComponent) {
    case INSTRUCTIONAL_DEPTH_COURSE_FORM:
      return (
        category === Types.HIGH_SCHOOL_TYPE ||
        category === Types.COMBO_SCHOOL_TYPE
      )
    case WEEKLY_MINUTES_COURSE_FORM:
      return category !== Types.HIGH_SCHOOL_TYPE
    default:
      return true
  }
}

export function isQuestionAnswered(question, school, { disciplines } = {}) {
  switch (question.displayComponent) {
    case INSTRUCTIONAL_DEPTH_COURSE_FORM:
      return hasCompleteHighSchoolCourses(school)
    case WEEKLY_MINUTES_COURSE_FORM:
      return hasCompleteKThroughEightCourses(school)
    case ARTS_INSTRUCTOR_FORM:
      return hasCompleteInstructors(school)
    case SINGLE_STANDARD_FORM:
    case MULTIPLE_STANDARDS_FORM:
      return !isEmpty(get(school, 'standards'))
    case COURSE_SCHEDULE_FORM:
      return !isEmpty(get(school, 'schedules'))
    case SPACE_TYPES_FORM:
      return !isEmpty(get(school, 'spaces'))
    case ARTS_EDUCATION_RESOURCES_FORM:
      return !isEmpty(get(school, 'artsEducationResources'))
    case INSTRUCTIONAL_TIME_MATRIX_FORM:
      return hasCompleteInstructionalTime(school, disciplines)
    case FUNDING_FORM:
      return !isEmpty(get(school, 'fundings'))
    case CUSTOM_QUESTION_FORM:
      return hasCustomAnswer(school, question.id)
    default:
      return true
  }
}

export function hasQuestionWithDisplayComponent(
  surveyQuestions,
  displayComponent
) {
  return (surveyQuestions || []).some(
    (question) => question.displayComponent === displayComponent
  )
}
